/**
 * Generation Service
 *
 * Handles communication with the generation API:
 * - Job status polling
 * - Result retrieval
 * - Error handling
 */

import { getAuthToken as getAuthTokenFromService, isAuthReady } from './auth-token';

/**
 * Get auth token from auth service.
 * Returns undefined if auth is not ready (allows graceful degradation).
 */
async function getAuthToken(forceRefresh?: boolean): Promise<string | undefined> {
  try {
    if (isAuthReady()) {
      return await getAuthTokenFromService(forceRefresh);
    }
  } catch (error) {
    console.error('[Generation Service] Failed to get auth token:', error);
  }
  return undefined;
}

/**
 * Partial assets available during generation (for progressive loading)
 */
export interface PartialAssets {
  baseSprite: string;
  textDescription?: string;
  availableAt: number;
}

export interface JobStatus {
  jobId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress: number;
  message?: string;
  result?: GenerationResult;
  error?: {
    message: string;
    failedAt?: string;
  };
  completedAt?: string;
  /** Partial assets for progressive loading */
  partialAssets?: PartialAssets;
}

export interface AnimationSet {
  idle: string;
  walk: string;
  attack: string;
  death: string;
  additional: string[];
}

export interface CombatAttribute {
  attributeId: string;
  name: string;
  category: string;
  spriteAnimationId: string;
  damageType: string;
  attackType: string;
  priority: number;
  effectFrames?: string[];
}

export interface GenerationResult {
  inputType: 'text' | 'draw' | 'upload';
  textDescription?: string;
  originalImage?: string;
  claudeAnalysis?: {
    concept: string;
    race: string;
    class: string;
    abilities: string[];
    stats: {
      health: number;
      attack: number;
      defense: number;
      speed: number;
    };
  };
  animations?: {
    animationSet: {
      idle: string;
      walk: string;
      attack: string;
      death: string;
      additional: string[];
    };
    totalAnimations: number;
  };
  combatAttributes?: {
    attributes: CombatAttribute[];
    totalExtracted: number;
    confidence: number;
  };
  baselineAttackType?: 'melee' | 'ranged';
  styleValidation?: {
    passed: boolean;
    colorSimilarity: number;
    shapeSimilarity: number;
    overallScore: number;
    feedback: string;
  };
  spriteImageBase64?: string;
  animationFrames?: string[];
  generatedAt: string;
  processingTimeMs: number;
}

/**
 * Poll job status until completion or failure
 *
 * @param jobId - Job ID to poll
 * @param onProgress - Callback for progress updates
 * @param options - Polling configuration
 * @returns Final job status when complete/failed
 */
export async function pollJobStatus(
  jobId: string,
  onProgress?: (status: JobStatus) => void,
  options: {
    pollInterval?: number;
    maxAttempts?: number;
  } = {}
): Promise<JobStatus> {
  const pollInterval = options.pollInterval || 2000; // 2 seconds
  const maxAttempts = options.maxAttempts || 450; // 15 minutes max (multi-directional generation with attack frames takes 6-7 mins)
  let attempts = 0;

  while (attempts < maxAttempts) {
    attempts++;

    try {
      const status = await getJobStatus(jobId);

      // Notify progress callback
      if (onProgress) {
        onProgress(status);
      }

      // Check if terminal state reached
      if (status.status === 'completed' || status.status === 'failed') {
        return status;
      }

      // Wait before next poll
      await delay(pollInterval);
    } catch (error) {
      console.error('[Generation Service] Polling error:', error);

      // If we've tried multiple times and keep failing, give up
      if (attempts > 3) {
        throw new Error('Failed to poll job status. Please try refreshing the page.');
      }

      // Otherwise, retry after delay
      await delay(pollInterval);
    }
  }

  throw new Error('Job status polling timed out. The generation may still be processing.');
}

/**
 * Get current status of a generation job
 *
 * @param jobId - Job ID to check
 * @returns Current job status
 */
export async function getJobStatus(jobId: string): Promise<JobStatus> {
  // Get auth token for authenticated request
  const token = await getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`/api/generate/${jobId}`, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Job not found. It may have been deleted or expired.');
    }

    // On 401, force-refresh the token and retry once
    if (response.status === 401) {
      console.warn('[Generation Service] Got 401, forcing token refresh and retrying...');
      const freshToken = await getAuthToken(true);
      if (freshToken) {
        const retryResponse = await fetch(`/api/generate/${jobId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${freshToken}`
          }
        });
        if (retryResponse.ok) {
          const retryData = await retryResponse.json();
          console.log(`[Generation Service] Job ${jobId} status (after token refresh):`, {
            status: retryData.status,
            progress: retryData.progress,
            hasPartialAssets: Boolean(retryData.partialAssets),
          });
          return retryData as JobStatus;
        }
      }
      throw new Error('Authentication failed. Please try signing in again.');
    }

    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch job status');
  }

  const data = await response.json();

  // Debug logging for progressive loading
  console.log(`[Generation Service] Job ${jobId} status:`, {
    status: data.status,
    progress: data.progress,
    hasPartialAssets: Boolean(data.partialAssets),
    partialAssetsSpriteLength: data.partialAssets?.baseSprite?.length
  });

  return data as JobStatus;
}

/**
 * In-progress job data for gallery display
 */
export interface InProgressJob {
  jobId: string;
  progress: number;
  status: 'queued' | 'processing';
  partialAssets?: {
    baseSprite: string;
    textDescription?: string;
  };
  startedAt?: string;
  textDescription?: string;
}

/**
 * Response from in-progress jobs endpoint
 */
export interface InProgressJobsResponse {
  jobs: InProgressJob[];
  limit: number;
  canCreateNew: boolean;
}

/**
 * Fetch in-progress generation jobs for the current user
 *
 * @param userId - User ID to fetch jobs for
 * @returns List of in-progress jobs and metadata
 */
export async function fetchInProgressJobs(userId: string): Promise<InProgressJobsResponse> {
  // Get auth token for authenticated request
  const token = await getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`/api/generate/in-progress?userId=${encodeURIComponent(userId)}`, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    // On 401, force-refresh the token and retry once
    if (response.status === 401) {
      const freshToken = await getAuthToken(true);
      if (freshToken) {
        const retryResponse = await fetch(`/api/generate/in-progress?userId=${encodeURIComponent(userId)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${freshToken}`
          }
        });
        if (retryResponse.ok) {
          return retryResponse.json();
        }
      }
      throw new Error('Authentication failed. Please try signing in again.');
    }

    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch in-progress jobs');
  }

  return response.json();
}

/**
 * Delay helper for polling
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
